import { inject } from '@angular/core'

import { Subject } from 'rxjs'

import { UserProfile } from '@ti/contracts'
import { DIALOG_FACADE } from '@ti/core/contracts/dialog'
import { ViewModel } from '@ti/core/models/view.model'

import { UserDirectoryKind, UserStoreEventBus, UserStoreEventType } from '../stores'

export abstract class BaseUserManager extends ViewModel {
  public readonly events$ = {
    updated: new Subject<string[] | void>(),
  }
  protected readonly dialogFacade = inject(DIALOG_FACADE)
  protected readonly userStoreEvents = inject(UserStoreEventBus)
  protected abstract readonly userDirectory: UserDirectoryKind
  // TODO: Move translation keys to method
  protected readonly createMessageSummaryKey: string = 'users.created-message.summary'
  protected readonly createMessageDetailKey: string = 'users.created-message.detail'
  protected readonly updateMessageSummaryKey: string = 'users.updated-message.summary'
  protected readonly updateMessageDetailKey: string = 'users.updated-message.detail'
  protected readonly deleteMessageSummaryKey: string = 'users.deleted-message.summary'
  protected readonly deleteMessageDetailKey: string = 'users.deleted-message.detail'
  protected readonly resendInviteMessageSummaryKey: string = 'users.invited-message.summary'
  protected readonly resendInviteMessageDetailKey: string = 'users.invited-message.detail'

  protected isResendInvitationLoading = false

  public async create(user: Partial<UserProfile> & { password?: string }) {
    const createdUser = await this.createUser(user)
    await this.displaySuccessMessage(
      this.createMessageSummaryKey,
      // Do not show an `invited` message in the notification
      !user.email || (user.username && user.password) ? '' : this.createMessageDetailKey,
      { email: user.email ?? '' },
    )
    this.events$.updated.next()
    if (createdUser) {
      this.userStoreEvents.publish({
        type: UserStoreEventType.Created,
        directory: this.userDirectory,
        user: createdUser,
        fleetId: createdUser.fleetId,
      })
    } else {
      this.userStoreEvents.publish({
        type: UserStoreEventType.Invalidate,
        directory: this.userDirectory,
        fleetId: user.fleetId,
      })
    }
  }

  public async update(user: UserProfile, data: Partial<UserProfile>) {
    await this.updateUser(user, data)
    await this.displaySuccessMessage(this.updateMessageSummaryKey, this.updateMessageDetailKey)
    this.events$.updated.next([user.id])
    this.publishUpdated(user)
  }

  public async resendInvitation(user: UserProfile) {
    if (!this.isResendInvitationLoading) {
      this.isResendInvitationLoading = true
      try {
        await this.sendInviteEmail(user)
        await this.displaySuccessMessage(
          this.resendInviteMessageSummaryKey,
          this.resendInviteMessageDetailKey,
          { email: user.email },
        )
        this.events$.updated.next([user.id])
        this.publishUpdated(user)
      } catch (error) {
        await this.errorHandler.notifyUser(error, 'Cannot resend the invite')
      } finally {
        this.isResendInvitationLoading = false
      }
    }
  }

  public async delete(user: UserProfile) {
    const labels = await this.getDeleteDialogLabels(user.name)
    const dialogResult = await this.dialogFacade.confirm({
      ...labels,
      confirmButtonColor: 'var(--color-alert-500)',
      confirmButtonIcon: 'pi-trash',
    })
    if (dialogResult) {
      try {
        await this.deleteUser(user)
        await this.displaySuccessMessage(
          this.deleteMessageSummaryKey,
          this.deleteMessageDetailKey,
          { name: user.name },
        )
        this.events$.updated.next()
        this.userStoreEvents.publish({
          type: UserStoreEventType.Deleted,
          directory: this.userDirectory,
          userId: user.id,
          fleetId: user.fleetId,
        })
      } catch (error) {
        await this.errorHandler.notifyUser(error, 'Cannot delete user')
      }
    }
  }

  public async disableMfa(user: UserProfile) {
    const labels = await this.getDisableMfaDialogLabels(user.name)
    const dialogResult = await this.dialogFacade.confirm({
      ...labels,
      confirmButtonColor: 'var(--color-alert-500)',
      confirmButtonIcon: 'pi pi-trash',
    })
    if (dialogResult) {
      try {
        await this.disableUserMfa(user)
        this.events$.updated.next([user.id])
        this.publishUpdated(user)
      } catch (e) {
        await this.errorHandler.notifyUser(e, 'Cannot disable user MFA')
      }
    }
  }

  protected abstract createUser(data: Partial<UserProfile>): Promise<UserProfile | void>

  protected abstract updateUser(user: UserProfile, data: Partial<UserProfile>): Promise<unknown>

  protected abstract deleteUser(user: UserProfile): Promise<unknown>

  protected abstract disableUserMfa(user: UserProfile): Promise<unknown>

  protected abstract sendInviteEmail(user: UserProfile): Promise<unknown>

  protected async getDeleteDialogLabels(name: string) {
    return this.languageService.massTranslate(
      {
        summary: 'users.delete-dialog.summary',
        description: 'users.delete-dialog.description',
        confirmButton: 'users.delete-dialog.confirm-button',
        cancelButton: 'users.delete-dialog.cancel-button',
      },
      { name },
    )
  }

  protected async getDisableMfaDialogLabels(name: string) {
    return this.languageService.massTranslate(
      {
        summary: 'users.disable-mfa-dialog.summary',
        description: 'users.disable-mfa-dialog.description',
        confirmButton: 'common.yes-disable',
      },
      { name },
    )
  }

  protected async displaySuccessMessage(summary: string, detail: string, ctx?: object) {
    const labels = await this.languageService.massTranslate({ summary, detail }, ctx)
    this.messageService.add({ ...labels, life: 5000, severity: 'success' })
  }

  protected publishUpdated(user: UserProfile): void {
    this.userStoreEvents.publish({
      type: UserStoreEventType.Updated,
      directory: this.userDirectory,
      userId: user.id,
      fleetId: user.fleetId,
    })
  }
}
